import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import { isEmptyString } from '@src/utils/stringUtils';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import { ToastId } from '@src/constants/toastId';
import InputField from '@src/components/atoms/textfield/inputField';
import SearchIcon from '@src/components/icons/search';
import LoadIcon from '@src/components/icons/loadIcon';
import EmptySearchCard from '@src/components/cards/emptySearchCard';
import { useModal } from '@src/components/modal-views/context';

type AutocompleteSearchProps<T> = {
  searchQuery: (term: string) => Promise<T[]>;
  placeholder?: string;
  renderItem: (item: T, index: number) => React.ReactNode;
  onItemClick: (item: T) => void;
  debounceDelay?: number;
};

const AutocompleteSearch = <T,>({
  searchQuery,
  placeholder = 'Search...',
  renderItem,
  onItemClick,
  debounceDelay = 500,
}: AutocompleteSearchProps<T>) => {
  const inputFieldRef = useRef<HTMLInputElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState<T[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const { closeModal } = useModal();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Keeps the props the debounced fetch reads current, and lets a slow response
  // for an older term be discarded instead of overwriting a newer one.
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;
  const latestTermRef = useRef('');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setIsSearching(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  const runSearch = useCallback(async (term: string) => {
    try {
      const results = await searchQueryRef.current(term);
      if (latestTermRef.current !== term) return;
      setData(results);
    } catch (e) {
      if (latestTermRef.current !== term) return;
      setData([]);
      toast.error('Failed to fetch search results', {
        toastId: ToastId.ERROR_TOAST,
      });
    } finally {
      if (latestTermRef.current === term) {
        setIsSearching(false);
      }
    }
  }, []);

  const debouncedSearch = useMemo(
    () => debounce(runSearch, debounceDelay),
    [runSearch, debounceDelay]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // The term is applied on every keystroke so the dropdown reacts immediately;
  // only the request itself is debounced.
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchTerm(value);
    latestTermRef.current = value;

    if (isEmptyString(value)) {
      debouncedSearch.cancel();
      setData([]);
      setIsSearching(false);
      return;
    }

    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    setIsOpen(true);
    setIsSearching(true);
    debouncedSearch(value);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 's') {
        event.preventDefault();
        setIsOpen(true);
        if (inputFieldRef.current) {
          inputFieldRef.current.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const hasNoResults =
    !isSearching && !isEmptyString(searchTerm) && data.length === 0;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 backdrop-blur-[3px] dark:bg-[#07090C03]"></div>
      )}
      <div
        ref={ref}
        className={cn(
          'relative',
          isOpen &&
            'z-50 rounded-t-[18px] bg-white dark:bg-dark-neutral-900 dark:bg-opacity-80'
        )}
      >
        <div
          className={cn(
            'p-2',
            isOpen &&
              'rounded-t-[18px] border border-b-0 dark:border-dark-neutral-200'
          )}
        >
          <InputField
            ref={inputFieldRef}
            inputfieldSize="small"
            placeholder={placeholder}
            preActions={[
              <SearchIcon
                key="searchIcon"
                className="text-dark-neutral-900 dark:text-white"
              />,
            ]}
            postActions={[
              isSearching ? (
                <LoadIcon
                  key="searching"
                  className="animate-spin text-dark-neutral-900 dark:text-white"
                />
              ) : (
                <p
                  key="shortcut"
                  className="body14 font-semibold dark:text-white"
                >
                  CTRL+S
                </p>
              ),
            ]}
            variant={hasNoResults ? 'error' : 'neutral'}
            onChange={handleInputChange}
            className="w-full xl:min-w-[500px]"
          />
        </div>
        {isOpen && (
          <div
            ref={containerRef}
            className={cn(
              'absolute top-[60px] flex max-h-[500px] w-full flex-col gap-2 overflow-scroll rounded-b-[18px] border border-t-0 bg-white px-3 py-2 dark:border-dark-neutral-200 dark:bg-dark-neutral-900 dark:bg-opacity-80',
              data.length === 0 && '!pb-0'
            )}
          >
            {isSearching && (
              <div
                className="sticky top-0 h-[3px] w-full overflow-hidden rounded-full bg-primary-100 dark:bg-dark-neutral-700"
                role="progressbar"
                aria-label="Searching"
              >
                <div className="h-full w-1/3 animate-indeterminate-progress rounded-full bg-brand" />
              </div>
            )}

            {data.length > 0 && (
              <div
                className={cn(
                  'flex flex-col gap-2',
                  isSearching && 'opacity-50'
                )}
              >
                <div className="flex items-center gap-2 dark:text-white">
                  <span className="body14 font-semibold">
                    Best matched DRep IDs
                  </span>
                  <span className="label12">{data.length + 1}+ results</span>
                </div>
                {data.map((item, index) => (
                  <div
                    onClick={() => {
                      closeModal();
                      onItemClick(item);
                      setIsOpen(false);
                    }}
                    className={cn('flex cursor-pointer flex-col')}
                    key={index}
                  >
                    {renderItem(item, index)}
                  </div>
                ))}
              </div>
            )}

            {data.length === 0 && isSearching && (
              <>
                <p className="label12 dark:text-white">Searching…</p>
                <div className="flex h-[147px] flex-col items-center justify-center gap-4 rounded-lg px-4 text-center dark:bg-dark-neutral-800">
                  <LoadIcon className="animate-spin text-brand dark:text-white" />
                  <p className="label12 dark:text-white">
                    Looking for DReps matching “{searchTerm}”…
                  </p>
                </div>
              </>
            )}

            {hasNoResults && (
              <>
                <p className="label12 dark:text-white">0 results</p>
                <EmptySearchCard />
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AutocompleteSearch;
